import React, { useId, useState } from "react";
import { Form } from "react-bootstrap";
import { FaEye, FaEyeSlash, FaExclamationTriangle } from "react-icons/fa";

/**
 * Form controls shared by Login and Signup.
 *
 * The auth pages had bare inputs: no affordance for revealing a password, no
 * warning when Caps Lock is on, and no read on whether a new password is any
 * good. All three are the cheapest wins available on a sign-in screen, so they
 * live here rather than being duplicated across the two pages.
 */

/** Text/email input with a leading icon. */
export function AuthField({ label, icon, className = "", ...props }) {
  const id = useId();
  return (
    <Form.Group className={`mb-3 ${className}`} controlId={id}>
      <Form.Label>{label}</Form.Label>
      <div className="auth-field">
        <span className="auth-field__icon" aria-hidden="true">
          {icon}
        </span>
        <Form.Control {...props} />
      </div>
    </Form.Group>
  );
}

/**
 * Password input with a reveal toggle and a Caps Lock warning.
 * The toggle is type="button" — inside a <form>, a bare <button> submits.
 */
export function PasswordField({
  label,
  icon,
  className = "",
  children,
  ...props
}) {
  const id = useId();
  const [shown, setShown] = useState(false);
  const [caps, setCaps] = useState(false);

  const trackCaps = (e) => {
    // getModifierState reads the real lock state, so it's correct even when
    // the key was pressed before this field was focused.
    if (typeof e.getModifierState === "function") {
      setCaps(e.getModifierState("CapsLock"));
    }
  };

  return (
    <Form.Group className={`mb-3 ${className}`} controlId={id}>
      <Form.Label>{label}</Form.Label>
      <div className="auth-field auth-field--pw">
        <span className="auth-field__icon" aria-hidden="true">
          {icon}
        </span>
        <Form.Control
          {...props}
          type={shown ? "text" : "password"}
          onKeyUp={trackCaps}
          onKeyDown={trackCaps}
          onBlur={(e) => {
            setCaps(false);
            props.onBlur?.(e);
          }}
        />
        <button
          type="button"
          className="auth-toggle"
          onClick={() => setShown((s) => !s)}
          aria-pressed={shown}
          aria-label={shown ? "Hide password" : "Show password"}
        >
          {shown ? <FaEyeSlash /> : <FaEye />}
        </button>
      </div>

      {/* aria-live so the warning is spoken, not only drawn */}
      <div aria-live="polite">
        {caps && (
          <p className="auth-caps">
            <FaExclamationTriangle aria-hidden="true" /> Caps Lock is on
          </p>
        )}
      </div>

      {children}
    </Form.Group>
  );
}

/* Four independent things worth checking, so the score maps 1:1 onto the four
   bars. Length counts twice because it matters more than any character class. */
export function scorePassword(pw) {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score += 1;
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) score += 1;
  return score;
}

const LEVELS = [
  { label: "Too short", cls: "is-none" },
  { label: "Weak", cls: "is-weak" },
  { label: "Fair", cls: "is-fair" },
  { label: "Good", cls: "is-good" },
  { label: "Strong", cls: "is-strong" },
];

/** Strength read-out for a new password. Renders nothing until typing starts. */
export function PasswordStrength({ value }) {
  if (!value) return null;
  const score = scorePassword(value);
  const level = LEVELS[score];

  return (
    <div className={`auth-strength ${level.cls}`}>
      <div className="auth-strength__bars" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={i < score ? "is-on" : ""} />
        ))}
      </div>
      <p className="auth-strength__label" aria-live="polite">
        Password strength: <strong>{level.label}</strong>
      </p>
    </div>
  );
}
